import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getIntegrationsStats, getMarketplaceHeroStats, listAttentionItems } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { severityRank } from '../../constants';
import { useAbortableLoad } from '../useAbortableLoad.js';

const EMPTY_STATS = {
  active_count: 0,
  needs_attention_count: 0,
  instances_count: 0,
  categories_count: 0,
};

const EMPTY_MARKETPLACE_STATS = {
  catalog_count: 0,
  categories_count: 0,
  official_count: 0,
  partner_count: 0,
  private_count: 0,
};

const failMessage = (err, fallback) => err?.message ?? fallback;

/**
 * Home data: workspace integration stats + the severity-ordered attention list
 * + Marketplace catalog counts (CT-4993). Marketplace failures stay isolated
 * from workspace stats / attention so the Already integrated hero still loads.
 */
export function useIntegrationsHome() {
  const { t } = useTranslation('admin-studio');

  const [stats, setStats] = useState(EMPTY_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [marketplaceStats, setMarketplaceStats] = useState(EMPTY_MARKETPLACE_STATS);
  const [marketplaceStatsLoading, setMarketplaceStatsLoading] = useState(true);

  const [attentionItems, setAttentionItems] = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [attentionError, setAttentionError] = useState(null);

  const run = useAbortableLoad();

  const load = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    setMarketplaceStatsLoading(true);
    setAttentionLoading(true);
    setAttentionError(null);

    await run(async (isStale) => {
      const failed = t('common.requestFailed');
      try {
        const [statsOutcome, attentionOutcome, marketplaceOutcome] = await Promise.allSettled([
          getIntegrationsStats(),
          listAttentionItems(),
          getMarketplaceHeroStats(),
        ]);
        if (isStale()) return;

        if (statsOutcome.status === 'fulfilled') {
          try {
            const normalizedStats = validateApiResponse(statsOutcome.value, failed);
            setStats({ ...EMPTY_STATS, ...(normalizedStats.body ?? {}) });
          } catch (err) {
            setStatsError(failMessage(err, failed));
            setStats(EMPTY_STATS);
          }
        } else {
          setStatsError(failMessage(statsOutcome.reason, failed));
          setStats(EMPTY_STATS);
        }

        if (attentionOutcome.status === 'fulfilled') {
          try {
            const normalizedAttention = validateApiResponse(attentionOutcome.value, failed);
            const rows = Array.isArray(normalizedAttention.body) ? normalizedAttention.body : [];
            setAttentionItems(
              [...rows].sort((a, b) => severityRank(a.status) - severityRank(b.status)),
            );
          } catch (err) {
            setAttentionError(failMessage(err, failed));
            setAttentionItems([]);
          }
        } else {
          setAttentionError(failMessage(attentionOutcome.reason, failed));
          setAttentionItems([]);
        }

        if (marketplaceOutcome.status === 'fulfilled') {
          try {
            const normalizedMarketplace = validateApiResponse(marketplaceOutcome.value, failed);
            setMarketplaceStats({ ...EMPTY_MARKETPLACE_STATS, ...(normalizedMarketplace.body ?? {}) });
          } catch {
            setMarketplaceStats(EMPTY_MARKETPLACE_STATS);
          }
        } else {
          setMarketplaceStats(EMPTY_MARKETPLACE_STATS);
        }
      } finally {
        if (!isStale()) {
          setStatsLoading(false);
          setMarketplaceStatsLoading(false);
          setAttentionLoading(false);
        }
      }
    });
  }, [t, run]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(
    () => ({
      stats,
      statsLoading,
      statsError,
      marketplaceStats,
      marketplaceStatsLoading,
      attentionItems,
      attentionLoading,
      attentionError,
      refetch: load,
    }),
    [
      stats,
      statsLoading,
      statsError,
      marketplaceStats,
      marketplaceStatsLoading,
      attentionItems,
      attentionLoading,
      attentionError,
      load,
    ],
  );
}
